import React, { useState, useEffect } from 'react';
import { Table, Button, Form, Modal, Alert, Spinner, Card } from 'react-bootstrap';
import { athleteService, userService } from '../../services/firebaseService';

const SimpleAthleteList = () => {
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAthletes, setSelectedAthletes] = useState([]);
  const [eligibility, setEligibility] = useState(null);
  const [newAthlete, setNewAthlete] = useState({
    firstName: '',
    lastName: '',
    gender: 'Homme',
    weight: '',
    dateOfBirth: '',
    club: ''
  });

  // Récupérer tous les athlètes et vérifier l'éligibilité
  useEffect(() => {
    const fetchAthletes = async () => {
      try {
        setLoading(true);
        
        // Vérifier l'éligibilité de l'utilisateur pour ajouter des athlètes
        const eligibilityData = await userService.checkPlanAccess('add_athletes');
        setEligibility(eligibilityData);
        
        // Récupérer les athlètes
        const athletesList = await athleteService.getAllAthletes();
        setAthletes(athletesList);
        
        setError(null);
      } catch (err) {
        console.error('Erreur lors de la récupération des athlètes:', err);
        setError('Impossible de charger les athlètes. Veuillez réessayer.');
      } finally {
        setLoading(false);
      }
    };

    fetchAthletes();
  }, []);

  // Gérer la création d'un nouvel athlète
  const handleCreateAthlete = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      // Validation simple
      if (!newAthlete.firstName || !newAthlete.lastName || !newAthlete.weight) {
        setError('Veuillez remplir tous les champs obligatoires.');
        setLoading(false);
        return;
      }
      
      // Vérifier si l'utilisateur peut ajouter un nouvel athlète
      const canAddAthlete = await userService.checkPlanAccess('add_athletes');
      if (!canAddAthlete.hasAccess) {
        setError(`Vous avez atteint la limite d'athlètes pour votre abonnement (${canAddAthlete.currentUsage}/${canAddAthlete.limit}). Veuillez mettre à niveau votre abonnement pour ajouter plus d'athlètes.`);
        setLoading(false);
        return;
      }
      
      // Créer l'athlète
      const createdAthlete = await athleteService.createAthlete({
        firstName: newAthlete.firstName,
        lastName: newAthlete.lastName,
        gender: newAthlete.gender,
        weight: parseFloat(newAthlete.weight),
        dateOfBirth: newAthlete.dateOfBirth,
        club: newAthlete.club || ''
      });
      
      // Mettre à jour la liste des athlètes
      setAthletes([createdAthlete, ...athletes]);
      
      // Mettre à jour l'éligibilité
      const updatedEligibility = await userService.checkPlanAccess('add_athletes');
      setEligibility(updatedEligibility);
      
      // Réinitialiser le formulaire
      setNewAthlete({
        firstName: '',
        lastName: '',
        gender: 'Homme',
        weight: '',
        dateOfBirth: '',
        club: ''
      });
      
      // Fermer le modal
      setShowCreateModal(false);
      setError(null);
    } catch (err) {
      console.error('Erreur lors de la création de l\'athlète:', err);
      setError(err.message || 'Impossible de créer l\'athlète. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  // Gérer la suppression d'un athlète
  const handleDeleteAthlete = async (athleteId) => {
    try {
      setLoading(true);
      await athleteService.deleteAthlete(athleteId);
      
      // Mettre à jour la liste des athlètes
      setAthletes(athletes.filter(athlete => athlete.id !== athleteId));
      setError(null);
    } catch (err) {
      console.error('Erreur lors de la suppression de l\'athlète:', err);
      setError(err.message || 'Impossible de supprimer l\'athlète. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  // Gérer la suppression multiple d'athlètes
  const handleDeleteMultipleAthletes = async () => {
    try {
      setLoading(true);
      
      // Supprimer chaque athlète sélectionné
      for (const athleteId of selectedAthletes) {
        await athleteService.deleteAthlete(athleteId);
      }
      
      // Mettre à jour la liste des athlètes
      setAthletes(athletes.filter(athlete => !selectedAthletes.includes(athlete.id)));
      
      // Réinitialiser la sélection
      setSelectedAthletes([]);
      setShowDeleteModal(false);
      setError(null);
    } catch (err) {
      console.error('Erreur lors de la suppression des athlètes:', err);
      setError(err.message || 'Impossible de supprimer les athlètes. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  // Gérer la sélection d'un athlète
  const handleSelectAthlete = (athleteId) => {
    if (selectedAthletes.includes(athleteId)) {
      setSelectedAthletes(selectedAthletes.filter(id => id !== athleteId));
    } else {
      setSelectedAthletes([...selectedAthletes, athleteId]);
    }
  };

  // Gérer la sélection de tous les athlètes
  const handleSelectAllAthletes = () => {
    if (selectedAthletes.length === athletes.length) {
      setSelectedAthletes([]);
    } else {
      setSelectedAthletes(athletes.map(athlete => athlete.id));
    }
  };

  return (
    <div className="simple-athlete-list">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Mes Athlètes</h2>
        <div>
          {selectedAthletes.length > 0 ? (
            <Button variant="danger" onClick={() => setShowDeleteModal(true)} className="me-2">
              Supprimer ({selectedAthletes.length})
            </Button>
          ) : null}
          <Button 
            variant="primary" 
            onClick={() => setShowCreateModal(true)}
            disabled={eligibility && !eligibility.hasAccess}
            title={eligibility && !eligibility.hasAccess ? `Limite d'athlètes atteinte (${eligibility.currentUsage}/${eligibility.limit})` : ""}
          >
            Ajouter un athlète
          </Button>
        </div>
      </div>

      {/* Afficher l'information sur l'abonnement */}
      {eligibility && (
        <Card className="mb-3">
          <Card.Body>
            <Card.Title>Votre abonnement</Card.Title>
            <Card.Text>
              <strong>Athlètes :</strong> {eligibility.currentUsage} / {eligibility.limit}
              {!eligibility.hasAccess && (
                <Alert variant="warning" className="mt-2">
                  Vous avez atteint la limite d'athlètes pour votre abonnement actuel. 
                  <a href="/subscription" className="ms-2">Mettre à niveau votre abonnement</a>
                </Alert>
              )}
            </Card.Text>
          </Card.Body>
        </Card>
      )}

      {error && <Alert variant="danger">{error}</Alert>}

      {loading && !athletes.length ? (
        <div className="text-center my-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Chargement...</span>
          </Spinner>
        </div>
      ) : (
        <>
          {athletes.length === 0 ? (
            <Alert variant="info">
              Vous n'avez pas encore d'athlètes. Créez-en un pour commencer.
            </Alert>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>
                    <Form.Check
                      type="checkbox"
                      checked={selectedAthletes.length === athletes.length}
                      onChange={handleSelectAllAthletes}
                    />
                  </th>
                  <th>Nom</th>
                  <th>Prénom</th>
                  <th>Genre</th>
                  <th>Poids (kg)</th>
                  <th>Date de naissance</th>
                  <th>Club</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {athletes.map(athlete => (
                  <tr key={athlete.id}>
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={selectedAthletes.includes(athlete.id)}
                        onChange={() => handleSelectAthlete(athlete.id)}
                      />
                    </td>
                    <td>{athlete.lastName}</td>
                    <td>{athlete.firstName}</td>
                    <td>{athlete.gender}</td>
                    <td>{athlete.weight}</td>
                    <td>{athlete.dateOfBirth ? new Date(athlete.dateOfBirth).toLocaleDateString() : 'Non spécifiée'}</td>
                    <td>{athlete.club}</td>
                    <td>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDeleteAthlete(athlete.id)}
                      >
                        Supprimer
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </>
      )}

      {/* Modal de création d'athlète */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Ajouter un athlète</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleCreateAthlete}>
            <Form.Group className="mb-3">
              <Form.Label>Prénom *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Prénom de l'athlète"
                value={newAthlete.firstName}
                onChange={(e) => setNewAthlete({...newAthlete, firstName: e.target.value})}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Nom *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Nom de l'athlète"
                value={newAthlete.lastName}
                onChange={(e) => setNewAthlete({...newAthlete, lastName: e.target.value})}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Genre *</Form.Label>
              <Form.Select
                value={newAthlete.gender}
                onChange={(e) => setNewAthlete({...newAthlete, gender: e.target.value})}
                required
              >
                <option value="Homme">Homme</option>
                <option value="Femme">Femme</option>
                <option value="Autre">Autre</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Poids (kg) *</Form.Label>
              <Form.Control
                type="number"
                step="0.1"
                placeholder="Poids en kg"
                value={newAthlete.weight}
                onChange={(e) => setNewAthlete({...newAthlete, weight: e.target.value})}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Date de naissance *</Form.Label>
              <Form.Control
                type="date"
                placeholder="Date de naissance"
                value={newAthlete.dateOfBirth}
                onChange={(e) => setNewAthlete({...newAthlete, dateOfBirth: e.target.value})}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Club</Form.Label>
              <Form.Control
                type="text"
                placeholder="Club de l'athlète"
                value={newAthlete.club}
                onChange={(e) => setNewAthlete({...newAthlete, club: e.target.value})}
              />
            </Form.Group>

            <div className="d-flex justify-content-end">
              <Button variant="secondary" className="me-2" onClick={() => setShowCreateModal(false)}>
                Annuler
              </Button>
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? 'Création...' : 'Créer'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Modal de confirmation de suppression multiple */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirmer la suppression</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Êtes-vous sûr de vouloir supprimer {selectedAthletes.length} athlète(s) ?
          Cette action est irréversible.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Annuler
          </Button>
          <Button variant="danger" onClick={handleDeleteMultipleAthletes} disabled={loading}>
            {loading ? 'Suppression...' : 'Supprimer'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SimpleAthleteList;
